import { useEffect, useRef, useState, type FormEvent } from 'react';
import { deviceKey, type ApiClient, type SignInAnswer } from '@despachador/api-client';
import { AUTH_ERRORS } from '@despachador/contracts/auth-errors';
import { Notice } from '../index';
import { type GoogleReturn } from './google-return';
import { AuthCard, authMessage, AuthSpinner, CodeInput, errorCode, type AuthBrand } from './pieces';
import { REAUTH_CANCELLED, useReauth } from './Reauth';
import { MOTIVE_OF_CODE, StatusCard, type StatusMotive } from './StatusScreen';

/** The single sentence of any failure of Google (docs/authentication.md «Un solo error»). */
export const GOOGLE_FAILED = AUTH_ERRORS.EXTERNAL_LOGIN_FAILED.user;

export interface GoogleLandingProps {
  api: ApiClient;
  brand: AuthBrand;
  ret: GoogleReturn;
  signedIn: boolean;
  platform: 'WEB' | 'PWA';
  large?: boolean;
  /** Sign-in with the ticket answered: the session or the second-factor step (same as a password sign-in). */
  onAnswer: (answer: SignInAnswer) => void;
  /** The credential was valid but the account cannot enter here (company suspended, other app). */
  onRefused: (motive: StatusMotive) => void;
  /** Leave the landing: to the sign-in screen (no session) or back where the person was. */
  onLeave: () => void;
}

/**
 * `/acceso/google`: where Google's return lands (the fragment was already taken out of the address
 * bar by `captureGoogleReturn`). A sign-in ticket is redeemed like a password sign-in; a link is
 * shown and confirmed explicitly; a re-authentication is answered (with the app's code when the
 * account has one) and the person repeats the action, because the navigation lost it. Every failure
 * of the provider is the same sentence.
 */
export function GoogleLanding(props: GoogleLandingProps) {
  const { ret, signedIn } = props;
  if (ret.kind === 'error') return <Failed {...props} text={GOOGLE_FAILED} />;
  // a sign-in ticket with a session already open is never redeemed (the gate goes back to the app)
  if (ret.kind === 'ticket') return signedIn ? null : <SignInWithTicket {...props} />;
  if (!signedIn) return <Failed {...props} text="Tu sesión terminó antes de volver de Google. Ingresa de nuevo y repite la acción." />;
  return ret.kind === 'link' ? <ConfirmLink {...props} /> : <ConfirmIdentity {...props} />;
}

function Failed({ brand, large, signedIn, onLeave, text }: GoogleLandingProps & { text: string }) {
  return (
    <StatusCard brand={brand} title="Ingreso con Google" tone="warn" large={large} action={{ label: signedIn ? 'Volver' : 'Volver al ingreso', onClick: onLeave }}>
      {text}
    </StatusCard>
  );
}

function SignInWithTicket({ api, brand, ret, platform, large, onAnswer, onRefused, onLeave }: GoogleLandingProps) {
  const [failed, setFailed] = useState<string | null>(null);
  const started = useRef<string | null>(null);
  useEffect(() => {
    // once per ticket (StrictMode runs effects twice; a ticket is single use)
    if (started.current === ret.value) return;
    started.current = ret.value;
    api.loginExternal({ ticket: ret.value, deviceKey: deviceKey(), platform }).then(onAnswer, (err) => {
      const c = errorCode(err);
      const motive = MOTIVE_OF_CODE[c ?? ''];
      if (motive) return onRefused(motive);
      // after a valid ticket these are told, like with a password; anything else is the one sentence
      if (c === 'MFA_ENROLLMENT_BLOCKED' || c === 'AUTH_UNAVAILABLE' || c === 'RATE_LIMITED' || c === 'NETWORK_ERROR') return setFailed(authMessage(err));
      setFailed(GOOGLE_FAILED);
    });
  }, [ret.value]); // eslint-disable-line react-hooks/exhaustive-deps
  if (failed)
    return (
      <StatusCard brand={brand} title="Ingreso con Google" tone="warn" large={large} action={{ label: 'Volver al ingreso', onClick: onLeave }}>
        {failed}
      </StatusCard>
    );
  return (
    <AuthSpinner label="Ingresando con Google" brand={brand} />
  );
}

/** `#link=`: which Google account came back, and an explicit confirmation before linking it. */
function ConfirmLink({ api, brand, ret, large, onLeave }: GoogleLandingProps) {
  const guard = useReauth();
  const [email, setEmail] = useState<string | null | undefined>(undefined);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const started = useRef<string | null>(null);
  useEffect(() => {
    if (started.current === ret.value) return;
    started.current = ret.value;
    api.account.google.inspect(ret.value).then(
      (r) => setEmail(r.email),
      (err) => setProblem(errorCode(err) === 'EXTERNAL_TICKET_EXPIRED' ? AUTH_ERRORS.EXTERNAL_TICKET_EXPIRED.user : authMessage(err)),
    );
  }, [ret.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await guard(() => api.account.google.confirm(ret.value));
      setDone(r.email ?? email ?? '');
    } catch (err) {
      const c = errorCode(err);
      if (c === REAUTH_CANCELLED) return;
      if (c === 'EXTERNAL_TICKET_EXPIRED' || c === 'EXTERNAL_ALREADY_LINKED' || c === 'EXTERNAL_LINK_EXISTS') setProblem(authMessage(err));
      else setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (problem)
    return (
      <StatusCard brand={brand} title="Vincular cuenta de Google" tone="warn" large={large} action={{ label: 'Volver', onClick: onLeave }}>
        {problem}
      </StatusCard>
    );
  if (done !== null)
    return (
      <AuthCard brand={brand} title="Cuenta de Google vinculada" done>
        <Notice tone="ok" urgent>
          Listo: {done ? <b>{done}</b> : 'tu cuenta de Google'} quedó vinculada. Desde ahora puedes ingresar con «Continuar con Google» o con tu contraseña.
        </Notice>
        <button type="button" className={`btn primary block ${large ? 'lg' : ''}`} onClick={onLeave}>
          Volver a mi cuenta
        </button>
      </AuthCard>
    );
  if (email === undefined)
    return (
      <AuthSpinner label="Comprobando la cuenta de Google" brand={brand} />
    );
  return (
    <AuthCard brand={brand} title="Vincular cuenta de Google" error={error} onSubmit={(e) => void confirm(e)}>
      <p style={{ margin: 0 }}>
        ¿Vincular la cuenta de Google {email ? <b className="nowrap">{email}</b> : 'que elegiste'} a tu usuario? Podrás ingresar con ella en lugar de tu contraseña. Si tienes la verificación en 2 pasos, se te
        seguirá pidiendo.
      </p>
      <button className={`btn primary block ${large ? 'lg' : ''}`} disabled={busy}>
        {busy ? 'Vinculando…' : 'Vincular'}
      </button>
      <button type="button" className="btn ghost sm" onClick={onLeave}>
        No vincular
      </button>
    </AuthCard>
  );
}

/**
 * `#reauth=`: the fresh Google sign-in confirms who is there (with the app's code when the account
 * has a second factor). The action that asked for it was lost with the navigation: the person repeats it.
 */
function ConfirmIdentity({ api, brand, ret, large, onLeave }: GoogleLandingProps) {
  const [needCode, setNeedCode] = useState(false);
  const [recovery, setRecovery] = useState(false);
  const [value, setValue] = useState('');
  const [state, setState] = useState<'working' | 'code' | 'done' | 'failed'>('working');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const send = async (extra: { code?: string; recoveryCode?: string } = {}) => {
    setBusy(true);
    setError(null);
    try {
      await api.account.reauth({ externalTicket: ret.value, ...extra });
      setState('done');
    } catch (err) {
      const c = errorCode(err);
      if (c === 'MFA_REQUIRED') {
        setNeedCode(true);
        setState('code');
      } else if (c === 'MFA_INVALID') {
        setValue('');
        setError(authMessage(err));
      } else {
        // anything else spends the ticket (only a missing or wrong code keeps it): start over from the action
        setError(c === 'EXTERNAL_TICKET_EXPIRED' ? AUTH_ERRORS.EXTERNAL_TICKET_EXPIRED.user : authMessage(err));
        setState('failed');
      }
    } finally {
      setBusy(false);
    }
  };
  const started = useRef<string | null>(null);
  useEffect(() => {
    if (started.current === ret.value) return;
    started.current = ret.value;
    void send();
  }, [ret.value]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === 'done')
    return (
      <AuthCard brand={brand} title="Identidad confirmada" done>
        <Notice tone="ok" urgent>
          Identidad confirmada con Google. Repite la acción que estabas haciendo: durante unos minutos no se te volverá a pedir.
        </Notice>
        <button type="button" className={`btn primary block ${large ? 'lg' : ''}`} onClick={onLeave}>
          Volver
        </button>
      </AuthCard>
    );
  if (state === 'failed')
    return (
      <StatusCard brand={brand} title="Confirma que eres tú" tone="warn" large={large} action={{ label: 'Volver', onClick: onLeave }}>
        {error ?? GOOGLE_FAILED}
      </StatusCard>
    );
  if (state === 'working' && !needCode)
    return (
      <AuthSpinner label="Confirmando tu identidad" brand={brand} />
    );
  return (
    <AuthCard
      brand={brand}
      title="Confirma que eres tú"
      error={error}
      onSubmit={(e) => {
        e.preventDefault();
        void send(recovery ? { recoveryCode: value.trim() } : { code: value });
      }}
    >
      <Notice tone="info">Tu cuenta tiene verificación en 2 pasos: escribe también el código de tu app.</Notice>
      <CodeInput value={value} onChange={setValue} recovery={recovery} autoFocus />
      <button className={`btn primary block ${large ? 'lg' : ''}`} disabled={busy || !value.trim()}>
        {busy ? 'Confirmando…' : 'Confirmar'}
      </button>
      <button
        type="button"
        className="btn ghost sm"
        onClick={() => {
          setRecovery(!recovery);
          setValue('');
          setError(null);
        }}
      >
        {recovery ? 'Usar el código de la app' : 'Usar un código de recuperación'}
      </button>
      <button type="button" className="btn ghost sm" onClick={onLeave}>
        Cancelar
      </button>
    </AuthCard>
  );
}
